import React, { useState, useEffect, useRef, useMemo } from 'react';
import Header from './common/header';
import { HiSearch, HiDownload, HiCheck, HiX, HiUpload } from 'react-icons/hi';
import { Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import AnalysisTable from './AnalysisTable';
import AnalysisChart from './AnalysisChart';
import SummaryChart from './SummaryChart';
import PortfolioMatrixModal from './PortfolioMatrixModal';
import ITMatrixModal from './ITMatrixModal';
import HistoricoRodadas from './HistoricoRodadas';
import '../styles/dadosUSP.css';
import { FaExclamationTriangle } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { HiBriefcase, HiBeaker } from 'react-icons/hi';

// Cache global para evitar requisições duplicadas (flags de carregamento)
const carregandoDados = {
    projetos: false,
    portfolios: false,
    its: false
};

const METRICAS_RESUMO = [
    { id: 'Prob previsão', label: 'Prob. previsão' },
    { id: 'Comparativo realizado', label: 'Comparativo realizado' },
    { id: 'Desvio percentual', label: 'Desvio percentual' },
    { id: 'Relaxamento', label: 'Relaxamento' },
];

const TRANSICOES_RESUMO = [
    { id: 'Gerais', label: 'Gerais' },
    { id: 'TRL', label: 'TRL' },
    { id: 'CRL', label: 'CRL' },
];

const GRUPOS_RESUMO = [
    { id: 'Geral', label: 'Geral' },
    { id: 'Grau de inovação', label: 'Grau de inovação' },
    { id: 'Tipologia', label: 'Tipologia' },
    { id: 'Gerência geral', label: 'Gerência geral' },
    { id: 'Categoria', label: 'Categoria' },
];

const RadioGroup = ({ titulo, opcoes, valor, onChange, opcoesDesabilitadas = [] }) => (
    <div>
        <div className="text-sm font-semibold text-gray-800 mb-1">{titulo}</div>
        <div className="space-y-0.5">
            {opcoes.map((op) => {
                const desabilitado = opcoesDesabilitadas.includes(op.id);
                const ativo = valor === op.id;
                return (
                    <label
                        key={op.id}
                        className={`flex items-center gap-2 text-sm leading-tight ${desabilitado
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 cursor-pointer hover:text-gray-900'
                            }`}
                    >
                        <input
                            type="radio"
                            checked={ativo}
                            disabled={desabilitado}
                            onChange={() => !desabilitado && onChange(op.id)}
                            className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span>{op.label}</span>
                    </label>
                );
            })}
        </div>
    </div>
);

/**
 * Monta a config do gráfico a partir das seleções.
 *
 * Lógica:
 *  - `transicoes` (Gerais/TRL/CRL) seleciona o bloco do resumoData (filtro de linhas).
 *  - `grupo` (Geral / Grau de inovação / ...) define a divisão das colunas:
 *      - "Geral"      → série única, X = transições.
 *      - "<divisor>"  → quebra por valor do divisor.
 *  - `separar` (Sim/Não) define se as colunas são quebradas por transições:
 *      - Sim → X = transições.
 *      - Não → X = valores do grupo (apenas válido com grupo != "Geral").
 *
 * Retorna null se a combinação não tem dados ou é inválida (ex: Geral+Não).
 */
const montarChart = (resumoData, { metrica, transicoes, grupo, separar }) => {
    if (!resumoData) return null;
    const bloco = resumoData[transicoes]?.[metrica];
    if (!bloco) return null;

    const filtroLabel = transicoes === 'Gerais' ? 'gerais' : transicoes;

    // Caso 1: Grupo=Geral.
    if (grupo === 'Geral') {
        if (separar) {
            // Separar=Sim: X = transições, série única.
            const dados = bloco.porTransicao;
            if (!dados || Object.keys(dados).length === 0) return null;
            return {
                titulo: `${metrica} por transição em dados ${filtroLabel}`,
                metrica,
                xLabel: 'Transição',
                dados,
                divisor: null,
            };
        }
        // Separar=Não: única coluna "Total" consolidando tudo.
        const dados = bloco.agregadoTotal;
        if (!dados || Object.keys(dados).length === 0) return null;
        return {
            titulo: `${metrica} em dados ${filtroLabel}`,
            metrica,
            xLabel: '',
            dados,
            divisor: null,
        };
    }

    // Caso 2: Grupo != Geral, Separar = Sim (X=transições, N séries pelo grupo).
    if (separar) {
        const dados = bloco.porTransicaoDivisor?.[grupo];
        const valido = dados && Object.values(dados).some(v => v && Object.keys(v).length > 0);
        if (!valido) return null;
        return {
            titulo: `${metrica} por transição e ${grupo} em dados ${filtroLabel}`,
            metrica,
            xLabel: 'Transição',
            dados,
            divisor: grupo,
        };
    }

    // Caso 3: Grupo != Geral, Separar = Não (X=valores do grupo, série única).
    const dados = bloco.porGrupo?.[grupo];
    if (!dados || Object.keys(dados).length === 0) return null;
    return {
        titulo: `${metrica} por ${grupo} em dados ${filtroLabel}`,
        metrica,
        xLabel: grupo,
        dados,
        divisor: null,
    };
};

const ResumoTab = ({ results, resumoData, resumoLoading, resumoError }) => {
    const [metrica, setMetrica] = useState(METRICAS_RESUMO[0].id);
    const [transicoes, setTransicoes] = useState('TRL');
    const [grupo, setGrupo] = useState('Geral');
    const [separar, setSeparar] = useState(true);

    const chart = useMemo(
        () => montarChart(resumoData, { metrica, transicoes, grupo, separar }),
        [resumoData, metrica, transicoes, grupo, separar]
    );

    if (!results) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">
                    Execute uma análise na aba "Seleção de projetos" para visualizar o resumo.
                </p>
            </div>
        );
    }

    if (resumoLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mr-3"></div>
                <span className="text-gray-600">Gerando resumo...</span>
            </div>
        );
    }

    if (resumoError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800 text-sm">{resumoError}</p>
            </div>
        );
    }

    if (!resumoData) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">Nenhum dado de resumo disponível.</p>
            </div>
        );
    }

    const valorSeparar = separar ? 'sim' : 'nao';

    return (
        <div className="space-y-5">
            <div className="border border-gray-200 rounded-lg bg-white px-4 py-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 items-start">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resultados</div>
                        <RadioGroup
                            titulo="Dados para analisar"
                            opcoes={METRICAS_RESUMO}
                            valor={metrica}
                            onChange={setMetrica}
                        />
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Filtrar os dados por</div>
                        <RadioGroup
                            titulo="Transições"
                            opcoes={TRANSICOES_RESUMO}
                            valor={transicoes}
                            onChange={setTransicoes}
                        />
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center">Colunas</div>
                        <div className="grid grid-cols-2 gap-x-4 items-start">
                            <RadioGroup
                                titulo="Grupos"
                                opcoes={GRUPOS_RESUMO}
                                valor={grupo}
                                onChange={setGrupo}
                            />
                            <RadioGroup
                                titulo="Separar por transições"
                                opcoes={[
                                    { id: 'sim', label: 'Sim' },
                                    { id: 'nao', label: 'Não' },
                                ]}
                                valor={valorSeparar}
                                onChange={(v) => setSeparar(v === 'sim')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                {chart
                    ? <SummaryChart {...chart} />
                    : (
                        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                            <p className="text-gray-500 text-sm">Sem dados para a combinação selecionada.</p>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

const Previsoes = () => {
    const [activeTab, setActiveTab] = useState('selecao');
    const [tipoAnalise, setTipoAnalise] = useState('previsoes'); // 'previsoes' ou 'metas'
    const [codigosProjetos, setCodigosProjetos] = useState('');
    const [trlMeta, setTrlMeta] = useState(9);
    const [crlMeta, setCrlMeta] = useState(9);
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [isExporting, setIsExporting] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    // Autocomplete: todos os projetos carregados ao montar a página
    const [todosProjetosMap, setTodosProjetosMap] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);

    // Modal de portfólios correlatos
    const [showPortfolioModal, setShowPortfolioModal] = useState(false);
    const [todosPortfolios, setTodosPortfolios] = useState({});

    // Modal de ITs correlatos
    const [showITModal, setShowITModal] = useState(false);
    const [todasIT, setTodasIT] = useState({});

    // Resumo (aba adicional para Análise de previsões)
    const [resumoData, setResumoData] = useState(null);
    const [resumoLoading, setResumoLoading] = useState(false);
    const [resumoError, setResumoError] = useState(null);

    // Verificar acesso do usuário e carregar todos os projetos e portfólios
    useEffect(() => {
        if (user && user.acesso !== 'Administrador' && user.acesso !== 'Verificado') {
            navigate('/');
            return;
        }

        if (!user) return;

        setShowWarningModal(true);

        // Carregar todos os projetos para autocomplete
        if (!carregandoDados.projetos) {
            carregandoDados.projetos = true;
            apiService.obterTodosProjetosBruto().then(response => {
                if (response.sucesso && response.projetos) {
                    setTodosProjetosMap(response.projetos);
                }
            }).catch(() => {
            }).finally(() => {
                carregandoDados.projetos = false;
            });
        }

        // Carregar todos os portfólios
        if (!carregandoDados.portfolios) {
            carregandoDados.portfolios = true;
            apiService.obterTodosPortfolios().then(response => {
                if (response.sucesso && response.portfolios) {
                    setTodosPortfolios(response.portfolios);
                }
            }).catch(() => {
            }).finally(() => {
                carregandoDados.portfolios = false;
            });
        }

        // Carregar todos os ITs
        if (!carregandoDados.its) {
            carregandoDados.its = true;
            apiService.obterTodosIT().then(response => {
                if (response.sucesso && response.iniciativasTecnologicas) {
                    setTodasIT(response.iniciativasTecnologicas);
                }
            }).catch(() => {
            }).finally(() => {
                carregandoDados.its = false;
            });
        }
    }, [user, navigate]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Projetos filtrados para o dropdown com base no ÚLTIMO termo digitado (após a última vírgula)
    const projetosFiltrados = useMemo(() => {
        const raw = codigosProjetos;
        const ultimaVirgula = raw.lastIndexOf(',');
        const termoAtual = ultimaVirgula === -1
            ? raw.trim()
            : raw.slice(ultimaVirgula + 1).trim();
        if (!termoAtual) return [];
        const termoLower = termoAtual.toLowerCase();
        return Object.keys(todosProjetosMap).filter(id =>
            String(id).toLowerCase().includes(termoLower)
        );
    }, [codigosProjetos, todosProjetosMap]);

    const handleSelecionarProjeto = (projectId) => {
        setCodigosProjetos(prev => {
            const atual = prev.trim();
            if (!atual) return projectId + ', ';
            // Pegar tudo antes da última vírgula (projetos já confirmados)
            const ultimaVirgula = atual.lastIndexOf(',');
            if (ultimaVirgula === -1) {
                // Só havia um termo sendo digitado, substitui pelo projeto selecionado
                return projectId + ', ';
            }
            // Mantém os projetos anteriores e substitui o termo atual pelo selecionado
            return atual.slice(0, ultimaVirgula + 1) + ' ' + projectId + ', ';
        });
        setShowDropdown(false);
        inputRef.current && inputRef.current.focus();
    };

    const tabs = useMemo(() => {
        const base = [
            { id: 'selecao', label: 'Seleção de projetos' },
            { id: 'resultados', label: 'Resultados' }
        ];
        if (tipoAnalise === 'previsoes') {
            base.push({ id: 'resumo', label: 'Resumo' });
        }
        return base;
    }, [tipoAnalise]);

    // Carrega o resumo quando o usuário abre a aba Resumo e há resultados.
    useEffect(() => {
        if (activeTab !== 'resumo') return;
        if (tipoAnalise !== 'previsoes') return;
        if (!results) return;
        if (resumoData || resumoLoading) return;

        setResumoLoading(true);
        setResumoError(null);
        apiService.obterResumoPrevisoes(results)
            .then((resp) => {
                if (resp && resp.erro) {
                    setResumoError(resp.erro);
                } else {
                    setResumoData(resp);
                }
            })
            .catch((err) => {
                setResumoError(err?.message || 'Erro ao carregar resumo.');
            })
            .finally(() => setResumoLoading(false));
    }, [activeTab, tipoAnalise, results, resumoData, resumoLoading]);

    // Limpa o resumo sempre que novos resultados chegam.
    useEffect(() => {
        setResumoData(null);
        setResumoError(null);
    }, [results]);

    const handleConfirmar = async () => {
        if (tipoAnalise === 'previsoes') {
            await handleConfirmarPrevisoes();
        } else if (tipoAnalise === 'metas') {
            await handleConfirmarMetas();
        }
    };

    const aguardarTarefa = async (tarefaId) => {
        const INTERVALO_MS = 5000;

        return new Promise((resolve, reject) => {
            const verificar = async () => {
                try {
                    const statusResponse = await apiService.verificarStatusTarefa(tarefaId);

                    if (statusResponse.status === 'concluido') {
                        resolve(statusResponse.resultado);
                    } else if (statusResponse.status === 'erro') {
                        reject(new Error(statusResponse.erro || 'Erro ao processar análise'));
                    } else {
                        setTimeout(verificar, INTERVALO_MS);
                    }
                } catch (err) {
                    reject(err);
                }
            };

            verificar();
        });
    };

    const handleConfirmarPrevisoes = async () => {
        setIsLoading(true);
        setError(null);

        try {
            let codigos = [];
            if (codigosProjetos.trim()) {
                codigos = codigosProjetos
                    .split(/[,\s\n]+/)
                    .map(cod => cod.trim())
                    .filter(cod => cod.length > 0);
            }

            console.log('Enviando para análise de previsões:', codigos.length === 0 ? 'todos os projetos' : codigos);
            const inicioResponse = await apiService.analisarPrevisoes(codigos);

            if (inicioResponse.error) {
                setError(inicioResponse.error);
                return;
            }

            const resultado = await aguardarTarefa(inicioResponse.tarefaId);
            setResults(resultado);
            setActiveTab('resultados');

        } catch (err) {
            setError(err.message || 'Erro ao processar análise de previsões');
            console.error('Falha na análise de previsões:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmarMetas = async () => {
        setIsLoading(true);
        setError(null);

        try {
            let codigos = [];
            if (codigosProjetos.trim()) {
                codigos = codigosProjetos
                    .split(/[,\s\n]+/)
                    .map(cod => cod.trim())
                    .filter(cod => cod.length > 0);
            }

            console.log('Enviando para análise de metas:', {
                codigos: codigos.length === 0 ? 'todos os projetos' : codigos,
                trlMeta,
                crlMeta
            });
            const inicioResponse = await apiService.analisarMetas(codigos, trlMeta, crlMeta);

            if (inicioResponse.error) {
                setError(inicioResponse.error);
                return;
            }

            const resultado = await aguardarTarefa(inicioResponse.tarefaId);
            setResults(resultado);
            setActiveTab('resultados');

        } catch (err) {
            setError(err.message || 'Erro ao processar análise de metas');
            console.error('Falha na análise de metas:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const parsed = JSON.parse(event.target.result);
                setResults(parsed);
                setActiveTab('resultados');
                setError(null);
            } catch {
                setError('Arquivo JSON inválido.');
            }
        };
        reader.readAsText(file);
        // Reset input so the same file can be re-imported if needed
        e.target.value = '';
    };

    const handleOpenPortfolioModal = () => {
        // Abrir modal imediatamente e carregar correlações em background
        setShowPortfolioModal(true);
    };

    const handleOpenITModal = () => {
        // Abrir modal imediatamente e carregar correlações em background
        setShowITModal(true);
    };

    const handleSavePortfolios = async (portfoliosCorrelatosMatrix) => {
        // Salvar a matriz no backend (persistir no arquivo)
        await apiService.salvarPortfoliosCorrelatos(portfoliosCorrelatosMatrix);
        console.log('Portfólios correlatos salvos:', portfoliosCorrelatosMatrix);
    };

    const handleSaveIT = async (itCorrelatosMatrix) => {
        // Salvar a matriz no backend (persistir no arquivo)
        await apiService.salvarITCorrelatos(itCorrelatosMatrix);
        console.log('IT correlatos salvos:', itCorrelatosMatrix);
    };

    const handleExport = async () => {
        if (!results) return;
        setIsExporting(true);
        setExportSuccess(false);
        try {
            const timestamp = new Date().toISOString().slice(0, 10);
            if (exportFormat === 'json') {
                const dataStr = JSON.stringify(results, null, 2);
                // Usar Blob para evitar problemas com URLs muito longas
                const blob = new Blob([dataStr], { type: 'application/json;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${tipoAnalise === 'metas' ? 'Análise de metas' : 'Análise de previsões'}_${timestamp}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                const workbook = new ExcelJS.Workbook();
                const ws = workbook.addWorksheet('Resultados');

                // Primeiro passo: encontrar as colunas no primeiro projeto que tiver dados
                let headerColumns = null;
                outer: for (const codigo of Object.keys(results)) {
                    for (const tipo of ['TRL', 'CRL', 'AMBOS']) {
                        const typeData = results[codigo][tipo];
                        if (typeData && typeData.columns && typeData.data) {
                            headerColumns = typeData.columns;
                            break outer;
                        }
                    }
                }
                if (!headerColumns) headerColumns = ['Status'];

                const statusPadding = new Array(Math.max(0, headerColumns.length - 1)).fill('');

                // Segundo passo: montar todas as linhas
                const allRows = [];
                Object.keys(results).forEach(codigo => {
                    const hasTRL = results[codigo].TRL?.columns && results[codigo].TRL?.data;
                    const hasCRL = results[codigo].CRL?.columns && results[codigo].CRL?.data;
                    const hasAMBOS = results[codigo].AMBOS?.columns && results[codigo].AMBOS?.data;

                    ['TRL', 'CRL', 'AMBOS'].forEach(tipo => {
                        const typeData = results[codigo][tipo];
                        if (typeData && typeData.columns && typeData.data) {
                            typeData.data.forEach(rowData => {
                                allRows.push([codigo, tipo, ...rowData]);
                            });
                        }
                    });

                    if (!hasTRL && !hasCRL && !hasAMBOS) {
                        const statusMsg = results[codigo].status || 'Sem dados disponíveis';
                        allRows.push([codigo, 'Status', statusMsg, ...statusPadding]);
                    }
                });

                ws.addRow(['Projeto', 'Tipo', ...headerColumns]);
                allRows.forEach(row => ws.addRow(row));
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(col => { col.width = 15; });

                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${tipoAnalise === 'metas' ? 'Análise de metas' : 'Análise de previsões'}_${timestamp}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            setExportSuccess(true);
            setTimeout(() => {
                setShowExportModal(false);
                setExportSuccess(false);
            }, 1500);
        } catch (error) {
            console.error('Erro ao exportar:', error);
            let errorMessage = 'Erro ao exportar. ';
            alert(errorMessage);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            {/* Modal de Aviso */}
            <Modal
                show={showWarningModal}
                onHide={() => setShowWarningModal(false)}
                size="md"
                centered
            >
                <Modal.Header className='bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center' style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px 8px 0 0',
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span style={{ fontWeight: 'bold' }}>Alerta</span>
                    <FaExclamationTriangle />
                </Modal.Header>
                <Modal.Body style={{ fontSize: '1.1rem', padding: '2rem' }}>
                    As funções incluídas nessa página ainda estão em desenvolvimento ou são de caráter experimental, use com cautela.
                </Modal.Body>
                <Modal.Footer style={{ borderTop: 'none' }}>
                    <Button
                        variant="warning"
                        onClick={() => setShowWarningModal(false)}
                        style={{ fontWeight: 'bold' }}
                    >
                        Entendi
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Exportação */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-t-lg">
                            <h3 className="text-lg font-semibold">Exportar resultados</h3>
                            <button onClick={() => setShowExportModal(false)} className="text-gray-300 hover:text-white transition-colors">
                                <HiX className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Selecione o formato de exportação:</h4>
                            <select
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                            >
                                <option value="xlsx">Excel</option>
                                <option value="json">JSON</option>
                            </select>
                        </div>
                        <div className="flex items-center justify-center space-x-4 px-6 pb-6">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-8 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                style={{ backgroundColor: '#003B6B' }}
                                className="flex items-center justify-center space-x-2 px-8 py-2 text-sm font-medium text-white rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            >
                                {isExporting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Exportando...</span>
                                    </>
                                ) : exportSuccess ? (
                                    <>
                                        <HiCheck className="w-4 h-4" />
                                        <span>Exportado!</span>
                                    </>
                                ) : (
                                    <>
                                        <HiDownload className="w-4 h-4" />
                                        <span>Exportar</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="h-screen flex flex-col overflow-hidden">
                <Header />

                {/* Botão Voltar */}
                <div className="flex-shrink-0 bg-gray-50" style={{ paddingTop: '20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/')}
                            style={{
                                backgroundColor: '#003B6B',
                                borderColor: '#003B6B',
                                padding: '10px 40px',
                                fontWeight: 'bold',
                                borderRadius: '8px'
                            }}
                        >
                            Voltar
                        </Button>
                    </div>
                </div>

                <div className="flex-1 bg-gray-50 overflow-hidden">
                    <div className="h-full ms-5 me-5">
                        <div className="flex h-full">

                            {/* Painel lateral - Funções */}
                            <div className="w-1/4 bg-white shadow-lg border-r border-gray-200 h-full flex flex-col">
                                <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 h-20 flex items-center justify-between flex-shrink-0">
                                    <span className="text-lg font-semibold">Funções</span>
                                </div>

                                <div className="bg-white px-6 py-4 border-b border-gray-200 flex-shrink-0">
                                    <div className="space-y-0">
                                        <button
                                            onClick={() => {
                                                setTipoAnalise('previsoes');
                                                setResults(null);
                                                setError(null);
                                                setResumoData(null);
                                                setResumoError(null);
                                                setActiveTab('selecao');
                                            }}
                                            className={`w-full text-left px-4 py-3 font-medium hover:bg-gray-50 transition-colors ${tipoAnalise === 'previsoes' ? 'text-blue-600 bg-blue-50' : 'text-gray-900'
                                                }`}
                                        >
                                            1. Análise de previsões
                                        </button>
                                        <button
                                            onClick={() => {
                                                setTipoAnalise('metas');
                                                setResults(null);
                                                setError(null);
                                                setResumoData(null);
                                                setResumoError(null);
                                                setActiveTab('selecao');
                                            }}
                                            className={`w-full text-left px-4 py-3 font-medium hover:bg-gray-50 transition-colors ${tipoAnalise === 'metas' ? 'text-blue-600 bg-blue-50' : 'text-gray-900'
                                                }`}
                                        >
                                            2. Análise de metas
                                        </button>
                                        <button
                                            onClick={() => setTipoAnalise('historico')}
                                            className={`w-full text-left px-4 py-3 font-medium hover:bg-gray-50 transition-colors ${tipoAnalise === 'historico' ? 'text-blue-600 bg-blue-50' : 'text-gray-900'}`}
                                        >
                                            3. Histórico de rodadas
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Área principal */}
                            {tipoAnalise === 'historico' ? <HistoricoRodadas /> : (
                            <div className="flex-1 bg-white h-full flex flex-col">
                                <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 h-20 flex items-center justify-between flex-shrink-0">
                                    <span className="text-lg font-semibold">
                                        {tipoAnalise === 'previsoes' ? 'Análise de previsões' : 'Análise de metas'}
                                    </span>
                                    <button
                                        onClick={() => fileInputRef.current.click()}
                                        style={{ border: '1px solid transparent', color: 'transparent', cursor: 'default' }}
                                        className="flex items-center space-x-2 px-4 py-2 rounded-lg"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    >
                                        <HiUpload className="w-4 h-4" />
                                        <span>Importar</span>
                                    </button>
                                </div>

                                {/* Abas */}
                                <div className="border-b border-gray-200 flex-shrink-0">
                                    <div className="flex justify-between items-center">
                                        <nav className="flex" aria-label="Tabs">
                                            {tabs.map((tab) => {
                                                return (
                                                    <button
                                                        key={tab.id}
                                                        onClick={() => setActiveTab(tab.id)}
                                                        className={`${activeTab === tab.id
                                                            ? 'border-blue-500 text-blue-600 bg-blue-50'
                                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                                            } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                );
                                            })}
                                        </nav>
                                        {results && activeTab !== 'resumo' && (
                                            <div className="px-6 py-4">
                                                <button
                                                    onClick={() => setShowExportModal(true)}
                                                    style={{ backgroundColor: '#003B6B' }}
                                                    className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                                                >
                                                    <HiDownload className="w-4 h-4" />
                                                    <span>Exportar</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Conteúdo das abas */}
                                <div className="flex-1 overflow-auto p-8">
                                    {activeTab === 'selecao' && (
                                        <div className="max-w-2xl mx-auto">
                                            <div className="mb-2">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center">
                                                        <span className="text-lg font-medium text-gray-900 mr-2">Buscar</span>
                                                        <HiSearch className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                    {user?.acesso === 'Administrador' && (
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                onClick={handleOpenPortfolioModal}
                                                                className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                                                title="Configurar portfólios correlatos"
                                                            >
                                                                <div className="flex items-center space-x-2">
                                                                    <HiBriefcase className="w-4 h-4 text-gray-500" />
                                                                    <span className="text-sm font-medium text-gray-700">Portfólio</span>
                                                                </div>
                                                            </button>
                                                            <button
                                                                onClick={handleOpenITModal}
                                                                className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                                                title="Configurar ITs correlatos"
                                                            >
                                                                <div className="flex items-center space-x-2">
                                                                    <HiBeaker className="w-4 h-4 text-gray-500" />
                                                                    <span className="text-sm font-medium text-gray-700">Iniciativa tecnológica</span>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mb-8 relative">
                                                <input
                                                    ref={inputRef}
                                                    type="text"
                                                    value={codigosProjetos}
                                                    onChange={(e) => {
                                                        setCodigosProjetos(e.target.value);
                                                        setShowDropdown(true);
                                                    }}
                                                    onFocus={() => {
                                                        if (codigosProjetos.trim()) setShowDropdown(true);
                                                    }}
                                                    placeholder="Digite os códigos dos projetos separados por vírgula"
                                                    className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                                {/* Dropdown de autocomplete */}
                                                {showDropdown && projetosFiltrados.length > 0 && (
                                                    <div
                                                        ref={dropdownRef}
                                                        className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1"
                                                        style={{ maxHeight: '220px', overflowY: 'auto' }}
                                                    >
                                                        {projetosFiltrados.map((projectId) => (
                                                            <div
                                                                key={projectId}
                                                                onMouseDown={(e) => {
                                                                    e.preventDefault(); // evita blur no input antes do click
                                                                    handleSelecionarProjeto(projectId);
                                                                }}
                                                                className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                            >
                                                                <span className="font-medium">{projectId}</span>
                                                                {todosProjetosMap[projectId] && (
                                                                    <span className="text-gray-400 ml-2">— {todosProjetosMap[projectId]}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Campos de TRL e CRL Meta (apenas para análise de metas) */}
                                            {tipoAnalise === 'metas' && (
                                                <div className="mb-8 space-y-4">
                                                    <div className="flex gap-4">
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                TRL Alvo
                                                            </label>
                                                            <select
                                                                value={trlMeta}
                                                                onChange={(e) => setTrlMeta(parseInt(e.target.value))}
                                                                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                            >
                                                                {[2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                                                    <option key={num} value={num}>{num}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                CRL Alvo
                                                            </label>
                                                            <select
                                                                value={crlMeta}
                                                                onChange={(e) => setCrlMeta(parseInt(e.target.value))}
                                                                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                            >
                                                                {[2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                                                    <option key={num} value={num}>{num}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="text-center">
                                                <button
                                                    onClick={handleConfirmar}
                                                    disabled={isLoading}
                                                    style={{ backgroundColor: "#003B6B" }}
                                                    className="hover:bg-blue-800 disabled:bg-blue-400 text-white px-8 py-3 rounded-md font-medium transition-colors duration-200"
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                            Processando...
                                                        </>
                                                    ) : (
                                                        'Confirmar'
                                                    )}
                                                </button>
                                            </div>

                                            {/* Error handling */}
                                            {error && (
                                                <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                                                    <p className="text-red-800 text-sm">{error}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'resultados' && (
                                        <div className="space-y-8">
                                            {results ? (
                                                <>
                                                    {/* TALVEZ BUGE --- VERIFICAR */}
                                                    {Object.keys(results).length > 1 && (
                                                        <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 bg-white shadow-sm max-w-sm">
                                                            <HiSearch className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                                            <input
                                                                type="text"
                                                                value={searchQuery}
                                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                                placeholder="Buscar projeto..."
                                                                className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
                                                            />
                                                            {searchQuery && (
                                                                <button
                                                                    onClick={() => setSearchQuery('')}
                                                                    className="ml-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                                                >
                                                                    <HiX className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Iterar pelos códigos de projetos */}
                                                    {Object.keys(results).length > 0 ? (
                                                        (() => {
                                                            const filtered = Object.keys(results).filter(codigo =>
                                                                codigo.toLowerCase().includes(searchQuery.toLowerCase())
                                                            );
                                                            return filtered.length > 0 ? filtered.map((codigo) => (
                                                                <div key={codigo} className="mb-12 border-b border-gray-200 pb-8">
                                                                    {/* Título do Projeto */}
                                                                    <div className="mb-8">
                                                                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                                                            {codigo}
                                                                        </h1>
                                                                        <div className="w-24 h-1 bg-gray-400 rounded"></div>
                                                                    </div>

                                                                    {/* Seção TRL do projeto */}
                                                                    {results[codigo].TRL && (
                                                                        <div className="mb-8">
                                                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                                                                                TRL
                                                                            </h2>
                                                                            <AnalysisTable
                                                                                data={results[codigo].TRL}
                                                                                type="trl"
                                                                                distribuicoes={results[codigo].Distribuicoes}
                                                                                codigoProjeto={codigo}
                                                                                mapa={results[codigo].mapa}
                                                                            />
                                                                            <AnalysisChart
                                                                                data={results[codigo].TRL}
                                                                                type="trl"
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {/* Seção CRL do projeto */}
                                                                    {results[codigo].CRL && (
                                                                        <div className="mb-8">
                                                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                                                                                CRL
                                                                            </h2>
                                                                            <AnalysisTable
                                                                                data={results[codigo].CRL}
                                                                                type="crl"
                                                                                distribuicoes={results[codigo].Distribuicoes}
                                                                                codigoProjeto={codigo}
                                                                                mapa={results[codigo].mapa}
                                                                            />
                                                                            <AnalysisChart
                                                                                data={results[codigo].CRL}
                                                                                type="crl"
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {/* Seção AMBOS do projeto */}
                                                                    {results[codigo].AMBOS && (
                                                                        <div className="mb-8">
                                                                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-purple-500 pb-2">
                                                                                Simulação Conjunta
                                                                            </h2>
                                                                            <AnalysisTable
                                                                                data={results[codigo].AMBOS}
                                                                                type="ambos"
                                                                                distribuicoes={results[codigo].Distribuicoes}
                                                                                codigoProjeto={codigo}
                                                                                mapa={results[codigo].mapa}
                                                                            />
                                                                            <AnalysisChart
                                                                                data={results[codigo].AMBOS}
                                                                                type="ambos"
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {/* Caso não haja dados TRL/CRL/AMBOS para o projeto */}
                                                                    {!results[codigo].TRL && !results[codigo].CRL && !results[codigo].AMBOS && (
                                                                        <div className="text-center py-8">
                                                                            <p className="text-gray-700">
                                                                                {results[codigo].status
                                                                                    ? results[codigo].status
                                                                                    : `Nenhum dado TRL/CRL/AMBOS disponível para ${codigo}.`}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )) : (
                                                                <div className="text-center py-8">
                                                                    <p className="text-gray-500">Nenhum projeto encontrado para "{searchQuery}".</p>
                                                                </div>
                                                            );
                                                        })()
                                                    ) : (
                                                        <div className="text-center py-8">
                                                            <p className="text-gray-500">Nenhum resultado disponível para exibição.</p>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="text-center py-8">
                                                    <p className="text-gray-500">Execute uma análise na aba "Seleção de projetos" para visualizar os resultados aqui.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'resumo' && (
                                        <ResumoTab
                                            results={results}
                                            resumoData={resumoData}
                                            resumoLoading={resumoLoading}
                                            resumoError={resumoError}
                                        />
                                    )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Input de arquivo oculto para importação */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleImport}
            />

            {/* Modal de Configuração de Portfólios Correlatos */}
            <PortfolioMatrixModal
                show={showPortfolioModal}
                onHide={() => setShowPortfolioModal(false)}
                portfolios={todosPortfolios}
                onSave={handleSavePortfolios}
            />

            {/* Modal de Configuração de ITs Correlatos */}
            <ITMatrixModal
                show={showITModal}
                onHide={() => setShowITModal(false)}
                iniciativasTecnologicas={todasIT}
                onSave={handleSaveIT}
            />
        </>
    );
};

export default Previsoes;
